import type {ParserT} from '@freckle/parser'
import {record, nullable, array, string, number, literal, firstOf} from '@freckle/parser'

export type SyncResultT =
  | {
      tag: 'SyncResultSuccess'
      contents: SyncResultSuccessT
    }
  | {
      tag: 'SyncResultFailure'
      contents: SyncResultFailureT
    }

export type SyncResultSuccessT = {
  schoolIdpId: number
  schoolName: string
  syncTime: number
  unchangedCounts: SnapshotCountsT
  updatedCounts: SnapshotCountsT
  deletedCounts: SnapshotCountsT
  addedCounts: SnapshotCountsT
  failedItems: FailedLogsT
}

type SnapshotCountsT = {
  courseMembershipCount: number
  studentCount: number
  teacherCount: number
  courseCount: number
}

const parseSnapshotCounts: ParserT<SnapshotCountsT> = record({
  courseMembershipCount: number(),
  studentCount: number(),
  teacherCount: number(),
  courseCount: number()
})

const parseLogMessage: ParserT<LogMessageT> = record({
  severity: firstOf(literal('error'), literal('warning')),
  message: string()
})

const parseLogPair: ParserT<LogPairT> = record({
  idpId: string(),
  logMessage: parseLogMessage,
  courseId: nullable(string()),
  studentId: nullable(string())
})

const parseFailedLogs: ParserT<FailedLogsT> = record({
  failedStudents: array(parseLogPair),
  failedTeachers: array(parseLogPair),
  failedCourseMemberships: array(parseLogPair),
  failedCourses: array(parseLogPair)
})

type FailedLogsT = {
  failedStudents: Array<LogPairT>
  failedTeachers: Array<LogPairT>
  failedCourseMemberships: Array<LogPairT>
  failedCourses: Array<LogPairT>
}

export type SyncResultFailureT = {
  schoolIdpId: number
  failure: LogMessageT
}

export type LogPairT = {
  idpId: string
  logMessage: LogMessageT
  courseId?: string | null
  studentId?: string | null
}

type LogMessageT = {
  severity: 'error' | 'warning'
  message: string
}

const parseSuccessContents: ParserT<SyncResultSuccessT> = record({
  schoolIdpId: number(),
  schoolName: string(),
  syncTime: number(),
  unchangedCounts: parseSnapshotCounts,
  updatedCounts: parseSnapshotCounts,
  deletedCounts: parseSnapshotCounts,
  addedCounts: parseSnapshotCounts,
  failedItems: parseFailedLogs
})

const parseFailureContents: ParserT<SyncResultFailureT> = record({
  schoolIdpId: number(),
  failure: parseLogMessage
})

const parseSuccess: ParserT<SyncResultT> = record({
  tag: literal('SyncResultSuccess'),
  contents: parseSuccessContents
})

const parseFailure: ParserT<SyncResultT> = record({
  tag: literal('SyncResultFailure'),
  contents: parseFailureContents
})

export const parseSyncResult: ParserT<SyncResultT> = firstOf(parseSuccess, parseFailure)
